import React, { useEffect, useState } from "react";
import { IUserDetails } from "../../exports/interfaces";
import { useNavigate, useParams } from "react-router-dom";
import { getTRoleClass } from "./UserTable";
import { Pencil } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import { darkenColor } from "../../exports/functions";
import { useRole } from "../../context/RoleContext";
import { usePermission } from "../../hooks/usePermission";

const ViewUser = ({ user }: { user: IUserDetails[] }) => {
  const { id } = useParams();
  const { statusOptions } = useOption();
  const { roles } = useRole();
  const { canEdit } = usePermission();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<IUserDetails>({
    userName: "",
    mobileNumber: "",
    email: "",
    nidNumber: "",

    companyName: "",
    department: "",
    position: "",

    userRole: "",
    userStatus: "",
    fileName: "",
    file: null,
  });

  const selectedRole = roles.find(
    (role) =>
      role.label === userInfo.userRole || role.value === userInfo.userRole,
  );

  useEffect(() => {
    if (!id) return;

    const selectedUser = user.find((item) => item.id === Number(id));

    if (selectedUser) {
      setUserInfo(selectedUser);
    }
  }, [id, user]);

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "User Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>User Profile</h3>
        </div>
      </div>
      <div className="dashboard-card">
        <div className="row g-3">
          {/* Left Panel */}
          <div className="col-lg-3 col-md-5">
            <div className="dashboard-card h-100">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="primary-color mb-0">User Information</h5>

                {canEdit("Users") && (
                  <button
                    className="btn btn-sm btn-badge-border px-3"
                    onClick={() => navigate(`/user/edit/${id}`)}
                  >
                    <i className="me-2">
                      <Pencil size={15} />
                    </i>
                    Edit
                  </button>
                )}
              </div>

              <div className="d-flex mb-3 justify-content-between gap-2">
                <span
                  className={`badge rounded-pill px-3 py-2 w-50 ${getTRoleClass(userInfo.userRole)}`}
                >
                  {userInfo.userRole}
                </span>

                <span
                  className={`badge rounded-pill px-3 py-2 w-50`}
                  style={statusStyle(userInfo.userStatus)}
                >
                  {userInfo.userStatus}
                </span>
              </div>

              <div className="d-flex mb-3 justify-content-between">
                <img src={`${process.env.REACT_APP_Url}/${userInfo?.documentUrl}`} alt="user" className="permission-user-image" />
                <div>
                  <h6 className="mb-1">{userInfo.userName}</h6>
                  <small>{userInfo.mobileNumber}</small>
                  <br />
                  <small className="text-primary">{userInfo.email}</small>
                  <br />
                  <small>{userInfo.nidNumber}</small>
                </div>
              </div>

              <hr />

              <h6 className="primary-color">User Details</h6>

              <div className="details-text mb-2">
                <small>Company</small>
                <p>{userInfo.companyName}</p>
              </div>

              <div className="details-text mb-2">
                <small>Department</small>
                <p>{userInfo.department}</p>
              </div>

              <div className="details-text mb-2">
                <small>Position</small>
                <p>{userInfo.position}</p>
              </div>

              <div className="details-text mb-2">
                <small>User Status</small>
                <p>{userInfo.userStatus}</p>
              </div>

              <div className="details-text">
                <small>User Role</small>
                <p>{userInfo.userRole}</p>
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="col-lg-9 col-md-7">
            <div className="dashboard-card">
              <h5 className="primary-color">Super Admin Permissions</h5>

              <small>
                System roles have full access and cannot be modified
              </small>

              <div className="mt-4 table-responsive permission-table table-checkbox">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th>View</th>
                      <th>Create</th>
                      <th>Edit</th>
                      <th>Delete</th>
                    </tr>
                  </thead>

                  <tbody>
                    {selectedRole?.models?.map((roleModel) => (
                      <tr key={roleModel.id}>
                        <td>{roleModel.model}</td>

                        <td>
                          <input
                            type="checkbox"
                            checked={roleModel.view}
                            readOnly
                          />
                        </td>

                        <td>
                          <input
                            type="checkbox"
                            checked={roleModel.create}
                            readOnly
                          />
                        </td>

                        <td>
                          <input
                            type="checkbox"
                            checked={roleModel.edit}
                            readOnly
                          />
                        </td>

                        <td>
                          <input
                            type="checkbox"
                            checked={roleModel.delete}
                            readOnly
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mt-3 table-checkbox">
                <div className="d-flex gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <input type="checkbox" checked readOnly />
                    Allowed
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <input type="checkbox" checked={false} readOnly />
                    Denied
                  </div>
                </div>

                <small>
                  Permission can only be edited by Authorized Users from the
                  settings page
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewUser;
